/**
 * AuthContext
 *
 * Provides authentication state and methods throughout the application.
 * Two login methods:
 *   - login(loginId, password, schoolId) -> for school users (SUPER_ADMIN/ADMIN/EMPLOYEE/STUDENT)
 *   - loginOwner(email, password)        -> for App Owner (platform-level admin)
 *
 * @module context/AuthContext
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import {
  api,
  setAccessToken,
  setSchoolId,
  clearAuth,
  getAccessToken,
  type ApiResponse,
} from '../services/api';
import { clearBadge } from '../utils/badge-api';
import { CRITICAL_BANNER_DISMISS_KEY } from '../components/notifications/CriticalBanner';
import { applyLanguageForRole, changeAppLanguage, getDeviceLanguage } from '../i18n'; // I18N-T1 / LANG-2

export type UserRole = 'SUPER_ADMIN' | 'ADMIN' | 'EMPLOYEE' | 'STUDENT' | 'APP_OWNER' | 'SUPPORT_STAFF';
export type EmployeeCategory = 'TEACHING' | 'NON_TEACHING' | 'ADMINISTRATIVE';

export interface User {
  id: string;
  schoolId: string | null;
  role: UserRole;
  /** OWNER-STAFF-LOGIN: owner-portal actor discriminator + support-staff permissions. */
  actorType?: 'OWNER' | 'SUPPORT_STAFF';
  permissions?: string[];
  loginId: string;
  name?: string;
  email?: string;
  phone?: string;
  profilePhotoUrl?: string;
  mustChangePassword: boolean;
  preferredLanguage?: string;
  /** LANG-2: null/undefined = the user has never explicitly chosen a language (preferredLanguage is
   *  the unset 'en' default). Drives the first-login device-choice adopt + the account-wins-if-set rule. */
  languageChosenAt?: string | null;
  employeeCategory?: EmployeeCategory;
  /** OLYMPIAD-P2G item 7: reserved-platform-tenant flag from the login payload, so the student
   *  shell scopes correctly on the FIRST render (no full-portal flash before SchoolContext resolves). */
  isPlatform?: boolean;
  studentId?: string;
  employeeId?: string;
  childrenIds?: string[];
  isParent?: boolean;
}

interface LoginCredentials {
  loginId: string;
  password: string;
  schoolId?: string;
  role?: UserRole;
}

interface OwnerLoginCredentials {
  email: string;
  password: string;
}

interface OwnerLoginResult {
  requires2FA: boolean;
  preAuthToken?: string;
}

export interface SchoolChoice {
  id: string;
  name: string;
}

export interface RoleChoice {
  role: UserRole;
  employeeCategory?: EmployeeCategory;
}

export type StaffLoginResult =
  | { multipleSchools: true; schools: SchoolChoice[] }
  | { multipleRoles: true; accounts: RoleChoice[] }
  | { requires2FA: true; challengeToken?: string }
  | { requires2FA: false; mustChangePassword: boolean };

type StaffLoginResponse = Partial<LoginResponseData> & {
  requires2FA?: boolean;
  challengeToken?: string;
  multipleSchools?: boolean;
  schools?: SchoolChoice[];
  multipleRoles?: boolean;
  accounts?: RoleChoice[];
};

type OwnerLoginResponse = Partial<LoginResponseData> & {
  requires2FA?: boolean;
  requiresTotp?: boolean;
  preAuthToken?: string;
};

/**
 * TEACHER-WARD-0 — another account on the SAME mobile in this school.
 * Sent only on a successful login, so it is never an enumeration oracle.
 */
export interface OtherAccountOnNumber {
  role: string;
  childCount: number;
}

/** Where the hint is parked for the session. Deliberately sessionStorage, not localStorage: it is a
 *  post-login notice, not durable state, and it must not outlive the tab or leak across accounts. */
export const OTHER_ACCOUNTS_KEY = 'aisistoo_other_accounts_on_number';

interface LoginResponseData {
  accessToken: string;
  user: User;
  inactivityTimeout?: number;
  otherAccountsOnThisNumber?: OtherAccountOnNumber[];
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<StaffLoginResult>;
  verifyStaff2FA: (challengeToken: string, code: string) => Promise<{ mustChangePassword: boolean }>;
  loginOwner: (credentials: OwnerLoginCredentials) => Promise<OwnerLoginResult>;
  verifyOwner2FA: (preAuthToken: string, code: string) => Promise<{ remainingRecoveryCodes?: number }>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  hasRole: (role: UserRole) => boolean;
  isTeacher: boolean;
  isNonTeaching: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

const USER_STORAGE_KEY = 'schoolsaas_user';

/**
 * LANG-2 — resolve + apply the UI language for a freshly-authenticated user, and handle the
 * first-login stamp. Non-students are forced to English (leak guard). For a STUDENT whose account
 * has NEVER been stamped (languageChosenAt null), adopt THIS device's pre-login choice and persist
 * it to the account in the background (PATCH stamps languageChosenAt), returning the user with the
 * stamp reflected so the cached copy converges and later reloads use the account preference.
 * Requires the access token to be set already (the background PATCH is authenticated).
 */
function resolveUserLanguage(u: User): User {
  applyLanguageForRole(u.role, u.preferredLanguage, u.languageChosenAt);
  if (u.role === 'STUDENT' && !u.languageChosenAt) {
    const lang = getDeviceLanguage();
    void changeAppLanguage(lang, { persistServer: true }); // best-effort PATCH → stamps languageChosenAt server-side
    return { ...u, preferredLanguage: lang, languageChosenAt: new Date().toISOString() };
  }
  return u;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const validateSession = async (): Promise<void> => {
      const token = getAccessToken();
      const cached = localStorage.getItem(USER_STORAGE_KEY);
      if (!token || !cached) { clearAuth(); setIsLoading(false); return; }
      let userData: User;
      try { userData = JSON.parse(cached) as User; }
      catch { clearAuth(); setIsLoading(false); return; }
      try {
        /*
         * [REQ-SEP-04 P0.2 / RC-4] No longer a discarded liveness probe. /auth/me now returns
         * the FULL session user through the same projection the login payload uses, and we WRITE
         * IT BACK — so a field added to the user shape in a deploy reaches every already-logged-in
         * device on its next boot instead of being `undefined` until a manual logout+login
         * (which used to hide feature-gated screens and mis-pick shells). The cached blob is the
         * fallback for old servers that still answer with the bare triple.
         */
        const meRes = await api.get<{ data: { user?: User } }>('/auth/me'); // interceptor handles 401 → refresh-or-redirect
        const freshUser = meRes.data?.data?.user;
        const merged = freshUser ? { ...userData, ...freshUser } : userData;
        const resolvedUser = resolveUserLanguage(merged); // LANG-2: apply + (rare) first-login stamp on restore
        setUser(resolvedUser);
        if (resolvedUser !== userData) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(resolvedUser));
        if (resolvedUser.schoolId) setSchoolId(resolvedUser.schoolId);
      } catch (probeError) {
        /*
         * [REQ-SEP-04 P0.1 / RC-1] The fail-open below is for TRANSPORT failures and 5xx only —
         * the machine being replaced during a deploy, a dead network. A 401/403 that reaches
         * this catch is a DEAD SESSION the interceptor could not repair (its own redirect
         * usually races ahead of us, but never rely on a race): rendering the cached user
         * anyway is how a device with an expired refresh cookie became a fully-drawn shell
         * where every query failed until a manual logout. Dead → clear and land on /login.
         */
        const status = (probeError as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
          clearAuth();
          setUser(null);
          setIsLoading(false);
          window.location.href = '/login';
          return;
        }
        // Network / 5xx: fail OPEN — keep the cached session; a genuinely dead token surfaces
        // (and now correctly signs out) on the next protected call.
        const resolvedUser = resolveUserLanguage(userData); // LANG-2: apply + (rare) first-login stamp on restore
        setUser(resolvedUser);
        if (resolvedUser !== userData) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(resolvedUser));
        if (resolvedUser.schoolId) setSchoolId(resolvedUser.schoolId);
      } finally { setIsLoading(false); }
    };

    void validateSession();
  }, []);

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<StaffLoginResult> => {
      const response = await api.post<ApiResponse<StaffLoginResponse>>(
        '/auth/login',
        credentials,
      );

      const data = response.data.data;

      if (data.multipleSchools) {
        return { multipleSchools: true, schools: data.schools ?? [] };
      }

      if (data.multipleRoles) {
        return { multipleRoles: true, accounts: data.accounts ?? [] };
      }

      if (data.requires2FA) {
        return { requires2FA: true, challengeToken: data.challengeToken };
      }

      setAccessToken(data.accessToken as string);
      if (data.user?.schoolId) {
        setSchoolId(data.user.schoolId);
      }
      const resolvedUser = resolveUserLanguage(data.user as User); // LANG-2: apply + first-login stamp
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(resolvedUser));
      setUser(resolvedUser);
      // A dismiss from a previous session in this same tab must not leak in.
      sessionStorage.removeItem(CRITICAL_BANNER_DISMISS_KEY);
      // TEACHER-WARD-0 — park the "your number also has a parent login" hint for this session.
      // Always written (removed when empty) so a stale hint cannot survive into a different
      // person's session in the same tab.
      if (data.otherAccountsOnThisNumber?.length) {
        sessionStorage.setItem(OTHER_ACCOUNTS_KEY, JSON.stringify(data.otherAccountsOnThisNumber));
      } else {
        sessionStorage.removeItem(OTHER_ACCOUNTS_KEY);
      }
      return { requires2FA: false, mustChangePassword: (data.user as User).mustChangePassword };
    },
    [],
  );

  const verifyStaff2FA = useCallback(
    async (challengeToken: string, code: string): Promise<{ mustChangePassword: boolean }> => {
      const response = await api.post<ApiResponse<LoginResponseData>>(
        '/auth/login/verify-2fa',
        { challengeToken, code },
      );

      const { user: userData, accessToken } = response.data.data;

      setAccessToken(accessToken);
      if (userData.schoolId) {
        setSchoolId(userData.schoolId);
      }
      const resolvedUser = resolveUserLanguage(userData); // LANG-2: apply + first-login stamp
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(resolvedUser));
      setUser(resolvedUser);
      // A dismiss from a previous session in this same tab must not leak in.
      sessionStorage.removeItem(CRITICAL_BANNER_DISMISS_KEY);
      return { mustChangePassword: userData.mustChangePassword };
    },
    [],
  );

const loginOwner = useCallback(
    async (credentials: OwnerLoginCredentials): Promise<OwnerLoginResult> => {
      const response = await api.post<ApiResponse<OwnerLoginResponse>>(
        '/owner/login',
        credentials,
      );

      const data = response.data.data;

      if (data.requiresTotp || data.requires2FA) {
        return { requires2FA: true, preAuthToken: data.preAuthToken };
      }

      setAccessToken(data.accessToken as string);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
      setUser(data.user as User);
      applyLanguageForRole('APP_OWNER', null); // owner portal stays English
      return { requires2FA: false };
    },
    [],
  );

  const verifyOwner2FA = useCallback(
    async (preAuthToken: string, code: string): Promise<{ remainingRecoveryCodes?: number }> => {
      const response = await api.post<ApiResponse<LoginResponseData>>(
        '/owner/login/verify-totp',
        { preAuthToken, code },
      );

      const data = response.data.data;
      const { user: userData, accessToken } = data;

      setAccessToken(accessToken);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
      setUser(userData);
      applyLanguageForRole('APP_OWNER', null); // owner portal stays English

      const { remainingRecoveryCodes } = data as { remainingRecoveryCodes?: number };
      return { remainingRecoveryCodes };
    },
    [],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    clearAuth();
    setUser(null);
    await clearBadge();
    sessionStorage.removeItem(CRITICAL_BANNER_DISMISS_KEY);
    window.location.href = '/login';
  }, []);

  const updateUser = useCallback((updates: Partial<User>): void => {
    setUser((prev) => {
      if (!prev) return null;
      const updated = { ...prev, ...updates };
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const hasRole = useCallback(
    (role: UserRole): boolean => user?.role === role,
    [user],
  );

  const isTeacher = useMemo(
    (): boolean =>
      user?.role === 'EMPLOYEE' && user?.employeeCategory === 'TEACHING',
    [user],
  );

  const isNonTeaching = useMemo(
    (): boolean =>
      user?.role === 'EMPLOYEE' &&
      (user?.employeeCategory === 'NON_TEACHING' ||
        user?.employeeCategory === 'ADMINISTRATIVE'),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      verifyStaff2FA,
      loginOwner,
      verifyOwner2FA,
      logout,
      updateUser,
      hasRole,
      isTeacher,
      isNonTeaching,
    }),
    [user, isLoading, login, verifyStaff2FA, loginOwner, verifyOwner2FA, logout, updateUser, hasRole, isTeacher, isNonTeaching],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };
export default AuthContext;
